/**
 * ActivityCard — Two-zone activity card (Earned + Spend)
 *
 * Replaces the three-zone ActivityZones (Supplies, Income, Expenses).
 * Earned zone = ART_INCOME transactions + UNCERTAIN w/ suggested income.
 * Spend zone = ART_SUPPLY + BUSINESS_EXPENSE + PERSONAL + UNCERTAIN w/ suggested spend.
 *
 * UNCERTAIN transactions appear as rows in their suggested zone (via tpSuggestedCategory)
 * but are NOT counted in totals. They show an orange "Review" label via InlineTransactionRow.
 * The actual confirm/change UI lives in InlineReviewSection outside this card.
 *
 * Uses AnimatedAccordion for expand/collapse with staggered row reveal.
 */

import { useMemo, useEffect, useRef, useState } from 'react';
import { T } from '@/styles/tokens';
import { CategoryIcon } from '@/components/CategoryIcon';
import { InlineTransactionRow } from '@/components/money/InlineTransactionRow';
import { AnimatedAccordion, StaggerRow } from '@/components/ui/AnimatedAccordion';
import { ReportedSaleRow } from '@/components/money/ReportedSaleRow';
import type { ArtTransaction } from '@/hooks/useArtTransactions';
import type { DashboardMode } from '@/components/money/ActivityZones';
import type { Verdict } from '@/types/verdict';
import type { MatchSuggestion } from '@/hooks/useMatchSuggestions';

interface ActivityCardProps {
  transactions: ArtTransaction[];
  /** UNCERTAIN transactions — NOT date-filtered, always show in zones */
  uncertainTransactions?: ArtTransaction[];
  onViewAll: (category: string) => void;
  onLinkIncome?: (txn: ArtTransaction) => void;
  onTapTransaction?: (txn: ArtTransaction) => void;
  animateKey: number;
  verdictTitleMap?: Record<string, string>;
  mode?: DashboardMode;
  /** Pool 2: sold verdicts awaiting bank payment */
  reportedVerdicts?: Verdict[];
  /** Pending match suggestions for Pool 2 verdicts */
  matchSuggestions?: MatchSuggestion[];
  onLinkMatch?: (verdictId: string, txnId: string) => void;
  onDismissMatch?: (suggestionId: string) => void;
  /** Unlimited current-month aggregates from useBurnProgress */
  unlimitedTotals?: {
    materialsCents: number;
    incomeCents: number;
    overheadCents: number;
    personalCents: number;
  };
}

/** Get tag label for any transaction */
function getTag(txn: ArtTransaction): string {
  switch (txn.tpCategory) {
    case 'ART_SUPPLY': return 'Materials';
    case 'BUSINESS_EXPENSE': return 'Overhead';
    case 'ART_INCOME': return txn.tpLinkedArtworkId ? 'Sale' : 'Teaching';
    case 'PERSONAL': return 'Personal';
    case 'UNCERTAIN': {
      const suggested = txn.tpSuggestedCategory;
      if (suggested === 'ART_SUPPLY') return 'Materials';
      if (suggested === 'BUSINESS_EXPENSE') return 'Overhead';
      if (suggested === 'ART_INCOME') return 'Income';
      if (suggested === 'PERSONAL') return 'Personal';
      return 'Uncategorized';
    }
    default: return 'Personal';
  }
}

/** Categories that route UNCERTAIN txns to the Earned zone */
const EARNED_CATEGORIES = ['ART_INCOME'];
/** Categories that route UNCERTAIN txns to the Spend zone */
const SPEND_CATEGORIES = ['ART_SUPPLY', 'BUSINESS_EXPENSE', 'PERSONAL'];


export function ActivityCard({
  transactions,
  uncertainTransactions: uncertainTxnsProp,
  onViewAll,
  onLinkIncome,
  onTapTransaction,
  animateKey,
  verdictTitleMap = {},
  mode = 'business',
  reportedVerdicts = [],
  matchSuggestions = [],
  onLinkMatch,
  onDismissMatch,
  unlimitedTotals,
}: ActivityCardProps) {
  const [pulse, setPulse] = useState(false);
  const prevKey = useRef(animateKey);

  useEffect(() => {
    if (animateKey !== prevKey.current) {
      prevKey.current = animateKey;
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 280);
      return () => clearTimeout(t);
    }
  }, [animateKey]);

  // UNCERTAIN transactions routed by tpSuggestedCategory.
  // Use the dedicated uncertainTransactions prop (NOT date-filtered) when available,
  // otherwise fall back to filtering from transactions (date-filtered — may miss old txns).
  const uncertainSource = uncertainTxnsProp ?? transactions.filter(t => t.tpCategory === 'UNCERTAIN');

  const uncertainEarned = useMemo(() =>
    uncertainSource.filter((t) => EARNED_CATEGORIES.includes(t.tpSuggestedCategory || '')),
    [uncertainSource]
  );
  const uncertainSpend = useMemo(() =>
    uncertainSource.filter((t) => SPEND_CATEGORIES.includes(t.tpSuggestedCategory || '')),
    [uncertainSource]
  );
  // UNCERTAIN with no/unknown suggestion — default to spend zone
  const uncertainUnknown = useMemo(() =>
    uncertainSource.filter((t) => !EARNED_CATEGORIES.includes(t.tpSuggestedCategory || '') && !SPEND_CATEGORIES.includes(t.tpSuggestedCategory || '')),
    [uncertainSource]
  );

  // Earned zone data (ART_INCOME + UNCERTAIN suggested as income)
  const earnedData = useMemo(() => {
    const confirmed = transactions.filter((t) => t.tpCategory === 'ART_INCOME');
    // Totals only count confirmed — UNCERTAIN doesn't affect the number
    const totalCents = unlimitedTotals?.incomeCents
      ?? confirmed.reduce((sum, t) => sum + Math.abs(t.amount), 0);
    const totalDollars = Math.round(totalCents / 100);
    // Merge confirmed + uncertain for display, confirmed first
    const allRows = [...confirmed, ...uncertainEarned];
    const recent = allRows.slice(0, 3);
    return { filtered: allRows, totalDollars, totalCount: allRows.length, confirmedCount: confirmed.length, recent };
  }, [transactions, unlimitedTotals, uncertainEarned]);

  // Spend zone data (ART_SUPPLY + BUSINESS_EXPENSE + conditional PERSONAL + UNCERTAIN spend)
  const spendData = useMemo(() => {
    const supplyTxns = transactions.filter((t) => t.tpCategory === 'ART_SUPPLY');
    const bizTxns = transactions.filter((t) => t.tpCategory === 'BUSINESS_EXPENSE');
    const perTxns = transactions.filter((t) => t.tpCategory === 'PERSONAL');

    const supplyCents = unlimitedTotals?.materialsCents
      ?? supplyTxns.reduce((sum, t) => sum + Math.abs(t.amount), 0);
    const bizCents = unlimitedTotals?.overheadCents
      ?? bizTxns.reduce((sum, t) => sum + Math.abs(t.amount), 0);
    const perCents = unlimitedTotals?.personalCents
      ?? perTxns.reduce((sum, t) => sum + Math.abs(t.amount), 0);

    // Confirmed transaction list respects mode
    const confirmedTxns = mode === 'everything'
      ? [...supplyTxns, ...bizTxns, ...perTxns].sort((a, b) => b.date.localeCompare(a.date))
      : [...supplyTxns, ...bizTxns].sort((a, b) => b.date.localeCompare(a.date));

    // Merge with uncertain spend + unknown-suggestion txns, confirmed first
    const allUncertainSpend = [...uncertainSpend, ...uncertainUnknown].sort((a, b) => b.date.localeCompare(a.date));
    const listTxns = [...confirmedTxns, ...allUncertainSpend];

    // Aggregate only counts confirmed — UNCERTAIN doesn't affect totals
    const aggregateCents = mode === 'everything'
      ? supplyCents + bizCents + perCents
      : supplyCents + bizCents;
    const aggregateDollars = Math.round(aggregateCents / 100);

    const recent = listTxns.slice(0, 4);

    return {
      supplyCents,
      bizCents,
      perCents,
      listTxns,
      aggregateDollars,
      totalCount: listTxns.length,
      confirmedCount: confirmedTxns.length,
      recent,
    };
  }, [transactions, mode, unlimitedTotals, uncertainSpend, uncertainUnknown]);

  return (
    <div>
      {/* ── Earned zone (ART_INCOME) ── */}
      <div>
        <AnimatedAccordion
          icon={<CategoryIcon category="ART_INCOME" size={36} muted />}
          label="Earned"
          total={`+$${earnedData.totalDollars.toLocaleString()}`}
          totalColor={T.primary}
          badge={uncertainEarned.length > 0 ? (
            <span style={{
              fontSize: 10, fontWeight: 700, color: '#92400E',
              background: 'rgba(224,122,61,0.12)', padding: '2px 8px',
              borderRadius: 10, fontFamily: T.fontFamily,
            }}>
              {uncertainEarned.length} to review
            </span>
          ) : undefined}
          innerBg="#F8F5EF"
          innerRadius="0"
        >
          {(showRows) => {
            const hasAnyIncome = earnedData.recent.length > 0 || reportedVerdicts.length > 0;
            const pool2Count = reportedVerdicts.length;
            return (
            <>
              {/* Empty state — only when BOTH pools are empty */}
              {!hasAnyIncome && (
                <StaggerRow showRows={showRows} index={0}>
                  {uncertainSource.length > 0 ? (
                    <div
                      onClick={() => onViewAll?.('UNCERTAIN')}
                      style={{
                        fontSize: 12, padding: '10px 20px', backgroundColor: T.sand,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ color: T.warning }}>
                        ⚠ {uncertainSource.length} transaction{uncertainSource.length !== 1 ? 's' : ''} need categorization
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: T.primary }}>Review</span>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: T.textMuted, padding: '8px 20px', backgroundColor: T.sand }}>
                      No income detected yet.
                    </div>
                  )}
                </StaggerRow>
              )}

              {/* Pool 2: Reported sales (awaiting payment) — render first */}
              {reportedVerdicts.map((v, i) => (
                <StaggerRow key={`p2-${v.id}`} showRows={showRows} index={i}>
                  <ReportedSaleRow
                    verdict={v}
                    matchSuggestion={matchSuggestions.find(s => s.verdictId === v.id && s.status === 'pending') || null}
                    onLinkMatch={onLinkMatch}
                    onDismissMatch={onDismissMatch}
                  />
                </StaggerRow>
              ))}

              {/* Pool 1 + Pool 3: Bank transactions (ART_INCOME) */}
              {earnedData.recent.map((txn, i) => {
                const isUncertain = txn.tpCategory === 'UNCERTAIN';
                return (
                  <StaggerRow key={txn.id} showRows={showRows} index={pool2Count + i}>
                    <InlineTransactionRow
                      txn={txn}
                      isIncome={true}
                      category={isUncertain ? (txn.tpSuggestedCategory || 'ART_INCOME') : 'ART_INCOME'}
                      linkedTitle={txn.tpLinkedArtworkId ? verdictTitleMap[txn.tpLinkedArtworkId] : null}
                      onLinkTap={() => onLinkIncome?.(txn)}
                      onTap={() => onTapTransaction?.(txn)}
                      showDivider={i > 0 || pool2Count > 0}
                      showTag={null}
                    />
                  </StaggerRow>
                );
              })}
              {earnedData.totalCount > 3 && (
                <StaggerRow showRows={showRows} index={earnedData.recent.length}>
                  <div
                    onClick={(e) => { e.stopPropagation(); onViewAll('ART_INCOME'); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: T.primary, padding: '6px 16px 4px 20px', cursor: 'pointer', backgroundColor: T.sand }}
                  >
                    See all {earnedData.totalCount} &rarr;
                  </div>
                </StaggerRow>
              )}
            </>
          );
          }}
        </AnimatedAccordion>
        <div style={{ height: 1, background: 'rgba(0,0,0,0.05)', margin: '0 16px' }} />
      </div>

      {/* ── Spend zone (ART_SUPPLY + BUSINESS_EXPENSE + PERSONAL) ── */}
      <div>
        <AnimatedAccordion
          icon={<CategoryIcon category="ART_SUPPLY" size={36} muted />}
          label="Spend"
          total={`$${spendData.aggregateDollars.toLocaleString()}`}
          badge={(uncertainSpend.length + uncertainUnknown.length) > 0 ? (
            <span style={{
              fontSize: 10, fontWeight: 700, color: '#92400E',
              background: 'rgba(224,122,61,0.12)', padding: '2px 8px',
              borderRadius: 10, fontFamily: T.fontFamily,
            }}>
              {uncertainSpend.length + uncertainUnknown.length} to review
            </span>
          ) : undefined}
          innerBg="#F8F5EF"
          innerRadius="0"
        >
          {(showRows) => (
            <>
              {spendData.recent.length === 0 ? (
                <StaggerRow showRows={showRows} index={0}>
                  <div style={{ fontSize: 12, color: T.textMuted, padding: '8px 20px', backgroundColor: T.sand }}>
                    No expenses detected yet.
                  </div>
                </StaggerRow>
              ) : (
                spendData.recent.map((txn, i) => (
                  <StaggerRow key={txn.id} showRows={showRows} index={i}>
                    <InlineTransactionRow
                      txn={txn}
                      isIncome={false}
                      category={txn.tpCategory}
                      onTap={() => onTapTransaction?.(txn)}
                      showDivider={i > 0}
                      showTag={null}
                    />
                  </StaggerRow>
                ))
              )}
              {spendData.totalCount > 4 && (
                <StaggerRow showRows={showRows} index={spendData.recent.length}>
                  <div
                    onClick={(e) => { e.stopPropagation(); onViewAll('SPEND'); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: T.primary, padding: '6px 16px 4px 20px', cursor: 'pointer', backgroundColor: T.sand }}
                  >
                    See all {spendData.totalCount} &rarr;
                  </div>
                </StaggerRow>
              )}
            </>
          )}
        </AnimatedAccordion>
      </div>
    </div>
  );
}